import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { ResourceSummary } from '@/types/user';

type WebsiteContextType = {
  websites: ResourceSummary[];
  selectedWebsite: ResourceSummary | null;
  setSelectedWebsite: (website: ResourceSummary | null) => void;
  hasWebsites: boolean;
};

const WebsiteContext = createContext<WebsiteContextType | undefined>(undefined);

const SELECTED_WEBSITE_KEY = 'selected_website_id';

export const WebsiteProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { userSession, isAuthenticated } = useAuth();
  const [selectedWebsiteId, setSelectedWebsiteId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(SELECTED_WEBSITE_KEY);
    } catch {
      return null;
    }
  });

  const websites = userSession?.resources || [];
  const hasWebsites = websites.length > 0;

  // Find selected website from resources
  const selectedWebsite = useMemo(() => {
    if (!hasWebsites) return null;

    // Try to find by stored ID
    if (selectedWebsiteId) {
      const found = websites.find(w => w.id === selectedWebsiteId);
      if (found) return found;
    }

    // Default to first website
    return websites[0] || null;
  }, [websites, selectedWebsiteId, hasWebsites]);

  // Update localStorage when selection changes
  useEffect(() => {
    if (selectedWebsite) {
      try {
        localStorage.setItem(SELECTED_WEBSITE_KEY, selectedWebsite.id);
      } catch {}
    }
  }, [selectedWebsite]);

  // Clear selection when logged out
  useEffect(() => {
    if (!isAuthenticated) {
      setSelectedWebsiteId(null);
      try {
        localStorage.removeItem(SELECTED_WEBSITE_KEY);
      } catch {}
    }
  }, [isAuthenticated]);

  const setSelectedWebsite = (website: ResourceSummary | null) => {
    setSelectedWebsiteId(website?.id || null);
  };

  const value = useMemo<WebsiteContextType>(() => ({
    websites,
    selectedWebsite,
    setSelectedWebsite,
    hasWebsites,
  }), [websites, selectedWebsite, hasWebsites]);

  return <WebsiteContext.Provider value={value}>{children}</WebsiteContext.Provider>;
};

export const useWebsite = () => {
  const ctx = useContext(WebsiteContext);
  if (!ctx) throw new Error('useWebsite must be used within a WebsiteProvider');
  return ctx;
};
